import { API_URL } from '../config/api';

export interface Skill {
  id: string;
  name: string;
  category?: string;
  description?: string;
}

export interface SkillsResponse {
  results: Skill[];
  total: number;
  page: number;
  size: number;
}

/**
 * Get skills for autocomplete
 * @param {string} token - Authorization token
 * @param {string} searchQuery - Search query for autocomplete
 * @param {number} page - Page number (default: 1)
 * @param {number} size - Page size (default: 50)
 * @returns {Promise<Skill[]>} List of skills
 */
export const getSkills = async (
  token: string,
  searchQuery: string = '',
  page: number = 1,
  size: number = 50
): Promise<Skill[]> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    if (searchQuery) {
      params.append('search_query', searchQuery);
    }

    const response = await fetch(`${API_URL}/users/skills?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: Skill[] = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching skills:', error);
    throw new Error(error.message || 'Failed to fetch skills');
  }
};

/**
 * Create new skill
 * @param {string} token - Authorization token
 * @param {Object} skillData - Skill data
 * @returns {Promise<Skill>} Created skill
 */
export const createSkill = async (
  token: string,
  skillData: {
    name: string;
    category?: string;
    description?: string;
  }
): Promise<Skill> => {
  try {
    console.log('📤 createSkill: Sending request to:', `${API_URL}/users/skills`);
    console.log('📤 createSkill: Payload:', JSON.stringify(skillData, null, 2));
    console.log('📤 createSkill: Token length:', token?.length || 0);

    const response = await fetch(`${API_URL}/users/skills`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(skillData)
    });

    console.log('📥 createSkill: Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ createSkill: Error response body:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}. Server response: ${errorText}`);
    }

    const data: Skill = await response.json();
    console.log('✅ createSkill: Success, created skill:', data);
    return data;
  } catch (error: any) {
    console.error('❌ Error creating skill:', error);
    throw new Error(error.message || 'Failed to create skill');
  }
};

/**
 * Get skill by ID
 * @param {string} token - Authorization token
 * @param {string} skillId - Skill ID
 * @returns {Promise<Skill>} Skill data
 */
export const getSkillById = async (
  token: string,
  skillId: string
): Promise<Skill> => {
  try {
    const response = await fetch(`${API_URL}/users/skills/${skillId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: Skill = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching skill:', error);
    throw new Error(error.message || 'Failed to fetch skill');
  }
};

/**
 * Update skill
 * @param {string} token - Authorization token
 * @param {string} skillId - Skill ID
 * @param {Object} skillData - Updated skill data
 * @returns {Promise<Skill>} Updated skill
 */
export const updateSkill = async (
  token: string,
  skillId: string,
  skillData: {
    name: string;
    category?: string;
    description?: string;
  }
): Promise<Skill> => {
  try {
    const response = await fetch(`${API_URL}/users/skills/${skillId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(skillData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: Skill = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error updating skill:', error);
    throw new Error(error.message || 'Failed to update skill');
  }
};

/**
 * Delete skill
 * @param {string} token - Authorization token
 * @param {string} skillId - Skill ID
 * @returns {Promise<void>}
 */
export const deleteSkill = async (
  token: string,
  skillId: string
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/users/skills/${skillId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('Error deleting skill:', error);
    throw new Error(error.message || 'Failed to delete skill');
  }
};

